import { createApi } from '@reduxjs/toolkit/query/react';

import { SuccessResponse } from '@/types/Success';

import { generateBaseQuseryWithReAuth } from '../baseQueryReauth';

export interface GetCodeRequest {
  customerId: number;
}

export interface GetCodeResponse {
  smsSendTo: string;
  emailSendTo: string;
  expiry: string;
}

export interface Skip2FaRequest {
  customerId: number;
}

export interface Enable2FaRequest {
  customerId: number;
  code: string;
  method: number;
}

export interface Enable2FaResponse {
  recoveryCodes: string[];
  status?: number;
}

export interface ResendCodeRequest {
  customerId: number;
  method: number;
}

export interface ResendCodeResponse {
  smsSendTo: string;
  emailSendTo: string;
  expiry: string;
}

// No request body needed - backend handles device trust via cookies
export interface Verify2FAForgotPasswordRequest {
  customerId: number;
  code: string;
  method: number;
}

export interface Login2FARequest {
  customerId: number;
  code: string;
  rememberMe: boolean;
}

export interface AuthTokenData2FA {
  accessToken: string;
  refreshToken: string;
  refreshTokenSerial: string;
  validTo: string;
  validFrom: string;
  customerId: number;
  is2FAEnabled: boolean;
  method2FA: number;
  customerTypeID: number;
  customerStatusID: number;
  is2FAVisited: boolean;
  status?: number;
}

export const twoFactorAuthenticationApi = createApi({
  reducerPath: 'twoFactorAuthenticationApi',
  keepUnusedDataFor: 300,
  baseQuery: generateBaseQuseryWithReAuth({ basePath: '' }),
  tagTypes: ['TwoFactorAuth'],
  endpoints: builder => ({
    getCode: builder.mutation<SuccessResponse<GetCodeResponse>, GetCodeRequest>({
      query: (body: GetCodeRequest) => ({
        url: '/TwoFactorAuthentication/getCode',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['TwoFactorAuth'],
    }),

    skip2Fa: builder.mutation<SuccessResponse<null>, Skip2FaRequest>({
      query: (body: Skip2FaRequest) => ({
        url: '/TwoFactorAuthentication/skip2Fa',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['TwoFactorAuth'],
    }),

    enable2Fa: builder.mutation<SuccessResponse<Enable2FaResponse>, Enable2FaRequest>({
      query: (body: Enable2FaRequest) => ({
        url: '/TwoFactorAuthentication/Enable2FA',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['TwoFactorAuth'],
    }),

    resendCode: builder.mutation<SuccessResponse<ResendCodeResponse>, ResendCodeRequest>({
      query: (body: ResendCodeRequest) => ({
        url: '/TwoFactorAuthentication/resendCode',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['TwoFactorAuth'],
    }),

    markDeviceAsTrusted: builder.mutation<SuccessResponse<null>, void>({
      query: () => ({
        url: '/TwoFactorAuthentication/markDeviceAsTrusted',
        method: 'POST',
      }),
      invalidatesTags: ['TwoFactorAuth'],
    }),
    verify2FAForgotPassword: builder.mutation<SuccessResponse<null>, Verify2FAForgotPasswordRequest>({
      query: (body: Verify2FAForgotPasswordRequest) => ({
        url: '/TwoFactorAuthentication/Verify2FAForgotPassword',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['TwoFactorAuth'],
    }),
    login2FA: builder.mutation<SuccessResponse<AuthTokenData2FA>, Login2FARequest>({
      query: (body: Login2FARequest) => ({
        url: '/Authentication/login2FA',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['TwoFactorAuth'],
    }),
  }),
});

export const {
  useGetCodeMutation,
  useSkip2FaMutation,
  useEnable2FaMutation,
  useResendCodeMutation,
  useMarkDeviceAsTrustedMutation,
  useVerify2FAForgotPasswordMutation,
  useLogin2FAMutation,
} = twoFactorAuthenticationApi;
